import { Product, MerchProductVariant } from './product';
import { VenueSection } from './venue';
import { api } from './store';
import { loadOrderWithItemGroups, OutgoingItemGroup } from './order';
import { Event, EventProductVariant } from './event';
import { Option } from '../components/Form/Select/Select';
import { apollo } from './graphql';
import { REMOVE_AND_BLOCK_ITEMS_FROM_ORDER, REMOVE_ITEMS_FROM_ORDER, UPDATE_ORDER_ITEMS } from './queries/item';

/* INTERFACES */
export interface OrderItem {
  invoiceId?: string | null;
  deliveryId?: string | null;
  incomingDeliveryId?: string | null;
  storageId?: string | null;
  productVariantId: string;
  seatIds?: string[];
  sectionId?: string;
  count?: number;
  price?: number | null;
  pool?: string;
  vatPercentage?: number;
  productId?: string | null;
  personalization?: string;
  shopId?: string | null;
}

export interface AddItem {
  incoming?: false;
  deliveryId?: string | null;
  storageLocationId?: string;
  invoiceId?: string | null;
  variantId?: string | null;
  productVariants?: MerchProductVariant[];
  count?: number | null;
  price?: number | null;
  sectionId?: string;
  shopId?: string | null;
}

export interface AddProductItem extends AddItem {
  productId?: string | null;
  product?: Product;
  vatPercentage?: number;
}
export interface AddEventItem extends AddItem {
  event?: Event;
  pool?: string;
  seatIds?: string[];
  pricingCategories?: Option[];
  selectedVariants?: EventProductVariant[];
  selectedSection?: VenueSection;
}

/* ORDER ITEMS CREATE */
export async function addItemToOrder(orderId: string, item: OrderItem): Promise<void> {
  await api.createOrderItem({ orderId, requests: [item] });
  await loadOrderWithItemGroups(orderId);
}

export type RefundItemsStrategy = 'refund_only' | 'refund_and_reissue' | 'refund_and_block';

export async function refundItems({
  orderId,
  itemIds,
  strategy,
  blockReason,
  markDeliveryAsSent,
}: {
  orderId: string;
  itemIds: string[];
  deliveryId?: string;
  invoiceId?: string;
  strategy?: RefundItemsStrategy;
  blockReason?: string;
  markDeliveryAsSent?: boolean;
}): Promise<void> {
  await api.createRefund({
    orderId,
    itemIds,
    strategy,
    blockReason,
    markDeliveryAsSent,
  });
  await loadOrderWithItemGroups(orderId);
}

/* ORDER ITEMS READ */
/* ORDER ITEMS UPDATE */
export async function editItems(orderId: string, itemIds: string[], model: Partial<OutgoingItemGroup>): Promise<void> {
  const variables = {
    orderId,
    itemIds,
    ...model,
  };
  await apollo.mutate({
    mutation: UPDATE_ORDER_ITEMS,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    variables: variables as any,
  });

  await loadOrderWithItemGroups(orderId);
}

export async function deleteItems(orderId: string, itemIds: string[]): Promise<void> {
  await api.destroyManyOrderItems({ orderId, itemIds });
  await loadOrderWithItemGroups(orderId);
}

/* Removes outgoing items, blocking blockedItemIds with the given reason and releasing the rest.
Incoming items are not reachable through this mutation and keep using deleteItems. */
export async function removeItems({
  orderId,
  itemIds,
  blockedItemIds = [],
  blockReason,
}: {
  orderId: string;
  itemIds: string[];
  blockedItemIds?: string[];
  blockReason?: string;
}): Promise<void> {
  if (blockedItemIds.length && blockReason) {
    if (itemIds.length) {
      await apollo.mutate({
        mutation: REMOVE_AND_BLOCK_ITEMS_FROM_ORDER,
        variables: { itemIds, blockedItemIds, blockReason },
      });
    } else {
      await apollo.mutate({
        mutation: REMOVE_ITEMS_FROM_ORDER,
        variables: { itemIds: blockedItemIds, blockReason },
      });
    }
  } else {
    await apollo.mutate({
      mutation: REMOVE_ITEMS_FROM_ORDER,
      variables: { itemIds },
    });
  }

  await loadOrderWithItemGroups(orderId);
}
